'use client';
import IconArrowRightBlue from '@/assets/icons/catalogs/icon-arrow-right-blue.svg';
import IconArrowUp from '@/assets/icons/catalogs/icon-arrow-up.svg';
import IconClose from '@/assets/icons/icon-round-close.svg';

import { CATEGORY_LIST_WITH_NESTED_CHILDREN_QUERY } from '@/apollo/graphql/queries/categoryQueries/categoryQueries';
import { CategoryData } from '@/apollo/graphql/types/categoryTypes';
import {
  CategoryCard,
  CustomCarouselCategory,
  PopularBrands,
  PopularCard,
  SharedCarousel,
} from '@/components';
import { decodeBase64Id } from '@/helpers/decodeBase64Id';
import { mapEdgesToItems } from '@/helpers/maps';
import { useLanguageCode } from '@/hooks/useLanguageCode';
import {
  useTranslatedData,
  useTranslatedDataWithNode,
} from '@/hooks/useTranslatedData';
import { Link } from '@/i18n/navigation';
import { useQuery } from '@apollo/client';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useWindowSize } from 'react-use';
import { Button, Drawer } from 'rsuite';
import styles from './category.module.scss';

interface CategoryContentProps {
  categoryId: string | null;
}

const CategoryContent = ({ categoryId }: CategoryContentProps) => {
  const filter = categoryId ? { ids: [categoryId] } : undefined;
  const t = useTranslations('Components.category-content');
  const languageCode = useLanguageCode();
  const locale = useLocale();

  const { data, loading } = useQuery<CategoryData>(
    CATEGORY_LIST_WITH_NESTED_CHILDREN_QUERY,
    {
      variables: { first: 1, filter, languageCode: languageCode },
    }
  );
  const mappedData = mapEdgesToItems(data?.categories);
  const translatedData = useTranslatedData(mappedData, ['name']);
  const category = translatedData?.[0] || null;
  const translatedChildren = useTranslatedDataWithNode(
    category?.children.edges,
    ['name']
  );

  const children = useMemo(() => {
    return translatedChildren || [];
  }, [category?.children?.edges]);

  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>(
    {}
  );

  const [openMobileVersion, setOpenMobileVersion] = useState(false);

  const { width } = useWindowSize();

  const openMobileMenu = () => {
    setOpenMobileVersion(!openMobileVersion);
  };
  const closeMobileMenu = () => {
    setOpenMobileVersion(false);
  };

  const handleShowMore = (categoryId: string, totalChildren: number) => {
    setVisibleCounts(prev => ({
      ...prev,
      [categoryId]: totalChildren,
    }));
  };

  useEffect(() => {
    const initialCounts = children.reduce(
      (acc, category) => {
        acc[category.node.id] = 5;
        return acc;
      },
      {} as Record<string, number>
    );
    setVisibleCounts(initialCounts);
  }, [children]);

  return (
    <div className={styles.productWrapper}>
      <div className={styles.headerSection}>
        {' '}
        {loading ? (
          <h2 className={`${styles.title} ${styles.skeleton}`}>
            <Skeleton />
          </h2>
        ) : (
          <h2 className={styles.title}>{category?.name}</h2>
        )}
        <Button className={styles.mobilePopupButton} onClick={openMobileMenu}>
          {t('category')}
        </Button>
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.leftSide}>
          <div className={styles.productCategories}>
            {loading ? (
              <>
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index}>
                    <h4
                      className={`${styles.categoriesTitle} ${styles.skeleton}`}
                    >
                      <Skeleton />
                    </h4>
                    {Array.from({ length: 1 }).map((_, index) => (
                      <ul className={styles.listItems} key={index}>
                        {Array.from({ length: 5 }).map((_, index) => (
                          <li
                            className={`${styles.item} ${styles.skeleton}`}
                            key={index}
                          >
                            <Skeleton />
                          </li>
                        ))}
                      </ul>
                    ))}
                  </div>
                ))}
              </>
            ) : (
              children.map(category => (
                <div key={category.node.id}>
                  <Link
                    href={`/category/${decodeBase64Id(category.node.id)}-${category.node.slug}`}
                  >
                    <h4 className={styles.categoriesTitle}>
                      {category.node.name}
                    </h4>
                  </Link>
                  {category.node.children?.edges.length > 0 && (
                    <ul className={styles.listItems}>
                      {category.node.children.edges
                        .slice(0, visibleCounts[category.node.id])
                        .map(child => (
                          <li key={child.node.id} className={styles.item}>
                            <Link
                              href={`/category/${decodeBase64Id(child.node.id)}-${child.node.slug}`}
                            >
                              {locale === 'ru'
                                ? child.node.name
                                : child?.node?.translation?.name}
                            </Link>
                          </li>
                        ))}
                      {visibleCounts[category.node.id] <
                        category.node.children.edges.length && (
                        <li
                          className={styles.moreItems}
                          onClick={() =>
                            handleShowMore(
                              category.node.id,
                              category.node.children.edges.length
                            )
                          }
                        >
                          {t('show-more')} (
                          {category.node.children.edges.length -
                            visibleCounts[category.node.id]}
                          ) <IconArrowUp />
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        <div className={styles.rightSide}>
          <CustomCarouselCategory />
          <div className={styles.popularCategories}>
            <div className={`${styles.popularCardsWrapper}`}>
              {loading
                ? Array.from({ length: 8 }).map((_, index) => (
                    <div
                      key={index}
                      className={` ${styles.skeletonForSidebarImg}`}
                    >
                      <Skeleton />
                    </div>
                  ))
                : children
                    .filter(category => !category.node.isPopular)
                    .slice(0, 8)
                    .map(category => (
                      <PopularCard
                        key={category?.node?.id}
                        title={category?.node?.name}
                        image={category?.node?.icon}
                        id={category?.node?.id}
                        slug={category?.node?.slug}
                      />
                    ))}
            </div>
          </div>
          {loading ? (
            <>
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className={styles.productCategory}>
                  <div className={`${styles.headerSection} ${styles.skeleton}`}>
                    <h3>
                      <Skeleton />
                    </h3>
                  </div>
                  <div className={styles.cardsWrapper}>
                    {Array.from({ length: 4 }).map((_, index) => (
                      <div className={`${styles.skeletonCard}`} key={index}>
                        <Skeleton />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            children.map(({ node: category }) => {
              if (
                category.products?.edges &&
                category.products.edges.length > 0
              ) {
                return (
                  <div key={category.id} className={styles.productCategory}>
                    <div className={styles.headerSection}>
                      <h3>{category.name || t('name-category')}</h3>
                      <Link
                        className={styles.link}
                        href={`/category/${decodeBase64Id(category.id)}-${category.slug}`}
                      >
                        {width <= 600 ? t('all') : t('show-all-products')}
                        <IconArrowRightBlue />
                      </Link>
                    </div>

                    <div className={styles.cardsWrapper}>
                      {category.products?.edges &&
                        category.products.edges.length > 0 &&
                        category.products.edges
                          .slice(0, 4)
                          .map(({ node: p }) => {
                            const labels =
                              p?.variants[0]?.labels?.edges?.map(
                                (edge: any) => edge?.node
                              ) || [];
                            const isWide = category.products.edges.length < 4;

                            return (
                              <CategoryCard
                                id={p?.variants[0]?.id}
                                productId={p?.id}
                                categorySlug={p?.category?.slug}
                                slug={p?.slug}
                                key={p?.id}
                                wide={isWide}
                                image={p?.variants[0]?.media[0]?.url}
                                productType={
                                  locale === 'ru'
                                    ? p?.category?.name
                                    : p?.category?.translation
                                      ? p?.category?.translation?.name
                                      : p?.category?.name
                                }
                                availability={p?.variants[0]?.availability}
                                labels={labels}
                                title={
                                  locale === 'uz'
                                    ? p?.variants[0]?.translation
                                      ? p?.variants[0]?.translation?.name
                                      : p?.name
                                    : p?.name
                                }
                                pricePerMonth={
                                  p?.variants[0]?.pricing?.installmentPrice12
                                    ?.amount
                                }
                                price={
                                  p?.variants[0]?.pricing?.price?.net?.amount ??
                                  0
                                }
                                priceUndiscounted={
                                  p?.variants[0]?.pricing?.priceUndiscounted
                                    ?.net?.amount ?? 0
                                }
                                discount={
                                  p?.variants[0]?.pricing?.discount?.net
                                    ?.amount ?? 0
                                }
                                bonus={p?.variants[0]?.pricing?.bonusesAmount}
                                className={styles.categoryCard}
                              />
                            );
                          })}
                    </div>
                  </div>
                );
              }
              return null;
            })
          )}
          <div className={styles.popularBrands}>
            <PopularBrands size={5} className={styles.popularBrandsItem} />
          </div>
        </div>
      </div>
      <div className={styles.shares}>
        <SharedCarousel sliderProgressDisable={false} showMoreButton={false} />
      </div>
      <Drawer
        size={width <= 400 ? 'full' : width <= 860 ? 'xs' : 'xs'}
        placement="right"
        open={openMobileVersion}
        onClose={() => setOpenMobileVersion(false)}
        closeButton={false}
      >
        <Drawer.Body style={{ padding: '0' }}>
          <div className={styles.mobileClose}>
            <h3>{t('category')}</h3>
            <button
              type="button"
              className={styles.drawer__closeButton}
              onClick={closeMobileMenu}
            >
              <IconClose />
            </button>
          </div>
          <div className={styles.drawerContent}>
            <div className={styles.productCategories}>
              {loading ? (
                <>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index}>
                      <h4
                        className={`${styles.categoriesTitle} ${styles.skeleton}`}
                      >
                        <Skeleton />
                      </h4>
                      {Array.from({ length: 1 }).map((_, index) => (
                        <ul className={styles.listItems} key={index}>
                          {Array.from({ length: 5 }).map((_, index) => (
                            <li
                              className={`${styles.item} ${styles.skeleton}`}
                              key={index}
                            >
                              <Skeleton />
                            </li>
                          ))}
                        </ul>
                      ))}
                    </div>
                  ))}
                </>
              ) : (
                children.map(category => (
                  <div key={category.node.id}>
                    <h4 className={styles.categoriesTitle}>
                      {category.node.name}
                    </h4>
                    {category.node.children?.edges.length > 0 && (
                      <ul className={styles.listItems}>
                        {category.node.children.edges
                          .slice(0, visibleCounts[category.node.id])
                          .map(child => (
                            <li
                              onClick={closeMobileMenu}
                              key={child.node.id}
                              className={styles.item}
                            >
                              <Link
                                href={`/category/${decodeBase64Id(child.node.id)}-${
                                  child.node.slug
                                }`}
                              >
                                {locale === 'ru'
                                  ? child.node.name
                                  : child?.node?.translation?.name}
                              </Link>
                            </li>
                          ))}

                        {visibleCounts[category.node.id] <
                          category.node.children.edges.length && (
                          <li
                            className={styles.moreItems}
                            onClick={() =>
                              handleShowMore(
                                category.node.id,
                                category.node.children.edges.length
                              )
                            }
                          >
                            {t('show-more')} (
                            {category.node.children.edges.length -
                              visibleCounts[category.node.id]}
                            ) <IconArrowUp />
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </Drawer.Body>
      </Drawer>
    </div>
  );
};

export default CategoryContent;
